import type {
  Card,
  Chassis,
  Constructor,
  Driver,
  DriverAffinity,
  SynergyBonus,
  SynergyReason,
  SynergyTag,
} from "../types";
import { isChassis, isDriver } from "../types";
import { HIST_TEAM } from "../data/drivers";
import { areTeammates } from "../data/teammates";

// Per-driver bonus for actually having raced for the chassis's constructor.
// Both are DRIVER attributes: a driver who knows the car is faster (pace) and
// more dialed-in (consistency). It used to add aero — a CAR stat — which was
// thematically backwards and spiked on aero tracks (Monaco: 71% of the bonus).
const HISTORIC_PACE = 10;
const HISTORIC_CONS = 4;

const droveFor = (driver: Driver, team: Constructor): boolean =>
  (HIST_TEAM[driver.name] ?? []).includes(team);

// True only when the two drivers actually raced as teammates in a real season —
// not merely when they each drove for the same constructor at some point.
const sharedTeam = (a: Driver, b: Driver): boolean => areTeammates(a.name, b.name);

/**
 * Per-driver historic-drive bonus: the edge a driver gets from having actually
 * raced for the chassis's constructor. Zero for any driver — including the
 * teammate — who never drove that chassis, so the bonus never leaks across the
 * garage.
 */
export function historicAffinity(driver: Driver, chassis: Chassis): DriverAffinity {
  return droveFor(driver, chassis.team)
    ? { pace: HISTORIC_PACE, consistency: HISTORIC_CONS }
    : { pace: 0, consistency: 0 };
}

/** Team-wide synergy bonus (shared, pairwise effects only). */
export function computeSynergy(d1: Driver, d2: Driver, chassis: Chassis): SynergyBonus {
  let consistency = 0,
    motor = 0;
  const reasons: SynergyReason[] = [];
  // Historic-drive contributes a reason per driver (for the lineup tags), but
  // its numeric bonus is applied per-driver via historicAffinity, not here.
  for (const d of [d1, d2]) {
    if (droveFor(d, chassis.team)) {
      reasons.push({ kind: "historicDrive", driver: d.name, team: chassis.team });
    }
  }
  if (d1.country === d2.country) {
    reasons.push({ kind: "sameNation", country: d1.country });
    consistency += 8;
  }
  if (sharedTeam(d1, d2)) {
    reasons.push({ kind: "sharedTeam", a: d1.name, b: d2.name });
    consistency += 6;
    motor += 3;
  }
  return { consistency, motor, reasons };
}

/**
 * What synergies would `card` create with the cards already picked this draft?
 * Drives the green tags shown on each card during the draft.
 */
export function synergyWith(card: Card, picked: readonly Card[]): SynergyTag[] {
  const tags: SynergyTag[] = [];
  const pickedDrivers = picked.filter(isDriver);
  const pickedChassis = picked.find(isChassis);
  if (isDriver(card)) {
    if (pickedChassis && droveFor(card, pickedChassis.team)) {
      tags.push({ kind: "droveFor", team: pickedChassis.team });
    }
    for (const d of pickedDrivers) {
      if (d.country === card.country) {
        tags.push({ kind: "sameNationAs", driver: d.name, country: d.country });
      }
      if (sharedTeam(card, d)) {
        tags.push({ kind: "exTeammate", driver: d.name });
      }
    }
  } else {
    for (const d of pickedDrivers) {
      if (droveFor(d, card.team)) {
        tags.push({ kind: "chassisDroveFor", driver: d.name, team: card.team });
      }
    }
  }
  return tags;
}
